import type { PositionLeg } from '@steward/shared';

export interface ConsolidatedLeg extends PositionLeg {
  /** Number of fill lots merged into this row. 1 for a leg that had no siblings. */
  fillCount: number;
}

/**
 * Collapses same-contract fill lots into one row per (underlying, expiration,
 * strike, option type, action) group. The rank-1 `/transactions` pipeline
 * records one row per FILL — a 10-lot leg filled 1+3+6 is three `PositionLeg`
 * rows — but Schwab's own positions view shows one row per contract at a
 * weighted-average price. This is DISPLAY ONLY (see task-20 brief): it must
 * never run anywhere near the derivation of `legs`/`positions` themselves,
 * only against what a table renders.
 *
 * Order is preserved by first occurrence of each group. A single-lot group
 * passes through unchanged apart from `fillCount: 1`.
 */
export function consolidateLegs(legs: PositionLeg[]): ConsolidatedLeg[] {
  const groups = new Map<string, PositionLeg[]>();

  for (const leg of legs) {
    const key = groupKey(leg);
    const group = groups.get(key);
    if (group) {
      group.push(leg);
    } else {
      groups.set(key, [leg]);
    }
  }

  const result: ConsolidatedLeg[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push({ ...group[0], fillCount: 1 });
      continue;
    }

    const quantity = sumBy(group, (leg) => leg.quantity);
    const quantityRemaining = sumBy(group, (leg) => leg.quantityRemaining);
    // Weighted by fill quantity, not fill count -- a 1-lot fill and a 9-lot
    // fill at different prices must not average as if they were equal-sized.
    const pricePerContract = quantity === 0
      ? 0
      : sumBy(group, (leg) => leg.pricePerContract * leg.quantity) / quantity;

    result.push({
      ...group[0],
      quantity,
      quantityRemaining,
      pricePerContract,
      fillCount: group.length,
    });
  }

  return result;
}

function sumBy(legs: PositionLeg[], pick: (leg: PositionLeg) => number): number {
  return legs.reduce((total, leg) => total + pick(leg), 0);
}

function groupKey(leg: PositionLeg): string {
  return [
    leg.underlyingSymbol,
    expirationKey(leg.expirationDate),
    leg.strikePrice,
    leg.optionType,
    leg.action,
  ].join('|');
}

function expirationKey(date: Date | string): string {
  return date instanceof Date ? date.toISOString() : new Date(date).toISOString();
}
